/**
 * The 23 `$PRES` dial icons, drawn from scratch as plain-stroke line icons.
 *
 * Not a reproduction of GoPro's own icon bitmap (gopro.github.io/labs/control/
 * custom/icons3.png) — that is their asset, not ours to ship. Same concepts
 * (a mountain is still a mountain), independently drawn, one colour
 * (`currentColor`) so they inherit the pressed/unpressed chip state for free.
 */

interface PresetIconGlyphProps {
  icon: number;
  className?: string;
}

const COMMON = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const PATHS: Record<number, React.ReactNode> = {
  0: (
    <>
      <line x1="4" y1="6" x2="20" y2="6" />
      <circle cx="9" cy="6" r="1.8" fill="currentColor" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <circle cx="15" cy="12" r="1.8" fill="currentColor" />
      <line x1="4" y1="18" x2="20" y2="18" />
      <circle cx="11" cy="18" r="1.8" fill="currentColor" />
    </>
  ),
  1: (
    <>
      <path d="M3 9c1.5-2 3.5-2 5 0s3.5 2 5 0 3.5-2 5 0 3.5 2 5 0" />
      <path d="M3 15c1.5-2 3.5-2 5 0s3.5 2 5 0 3.5-2 5 0 3.5 2 5 0" />
    </>
  ),
  2: (
    <>
      <circle cx="6" cy="17" r="3.2" />
      <circle cx="18" cy="17" r="3.2" />
      <path d="M6 17l4-8.5h3.5M9.2 8.5l4.8 8.5M18 17l-3-9h-2" />
    </>
  ),
  3: (
    <>
      <path d="M7 21v-6.2a2 2 0 012-2v0a2 2 0 012 2V13a2 2 0 014 0v3.2a2 2 0 014 0V19a2 2 0 01-2 2H9" />
      <path d="M9 12.8V6a1.3 1.3 0 012.6 0v6.8" />
    </>
  ),
  4: (
    <>
      <path d="M4 11l8-6 8 6" />
      <path d="M6 10v9h12v-9" />
      <path d="M10 19v-5h4v5" />
    </>
  ),
  5: (
    <>
      <path d="M4 16a8 8 0 0116 0" />
      <path d="M12 16l4-4" />
      <circle cx="12" cy="16" r="1.2" fill="currentColor" />
    </>
  ),
  6: (
    <>
      <path d="M4 17c3 1 4-3 7-2s4 3 7 1" />
      <circle cx="19" cy="7" r="2" />
      <path d="M19 9v3" />
    </>
  ),
  7: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v2M12 19v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M3 12h2M19 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
    </>
  ),
  8: (
    <>
      <rect x="3" y="6" width="10" height="7" rx="1" />
      <rect x="13" y="10" width="8" height="6" rx="1" />
      <path d="M6 17h4" />
    </>
  ),
  9: (
    <>
      <path d="M4 4h3M4 4v3M20 4h-3M20 4v3M4 20h3M4 20v-3M20 20h-3M20 20v-3" />
      <circle cx="12" cy="10" r="2.4" />
      <path d="M8 17c0-2.5 1.8-4 4-4s4 1.5 4 4" />
    </>
  ),
  10: (
    <>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4.4" />
      <circle cx="12" cy="12" r="1.1" fill="currentColor" />
    </>
  ),
  11: (
    <>
      <path d="M12 3v18M4.5 7.5l15 9M19.5 7.5l-15 9" />
      <path d="M12 3l-1.6 1.6M12 3l1.6 1.6M12 21l-1.6-1.6M12 21l1.6-1.6" />
    </>
  ),
  12: (
    <>
      <path d="M3 19l6-10 3 4 3-6 6 12z" />
      <path d="M12 19l3-5 2 2" />
    </>
  ),
  13: <path d="M3 12l18-7-7 18-2-7-7-2z" />,
  14: <path d="M12 3c4 5 7 8.5 7 12a7 7 0 01-14 0c0-3.5 3-7 7-12z" />,
  15: (
    <>
      <path d="M4 12a8 8 0 0113.9-5.4" />
      <path d="M20 12a8 8 0 01-13.9 5.4" />
      <path d="M17 3v4h-4M7 21v-4h4" />
    </>
  ),
  16: (
    <>
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
      <path d="M19 15l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z" />
    </>
  ),
  17: (
    <>
      <circle cx="14.5" cy="4.5" r="1.6" fill="currentColor" />
      <path d="M10 21l2-5 3 2 3 4" />
      <path d="M9 13l3-3 2 2 4-1" />
      <path d="M12 10l-3 2-3-1" />
    </>
  ),
  18: (
    <>
      <path d="M12 16V4" />
      <path d="M7 9l5-5 5 5" />
      <path d="M5 16v3a1 1 0 001 1h12a1 1 0 001-1v-3" />
    </>
  ),
  19: (
    <>
      <path d="M12 21v-8" />
      <path d="M12 13c0-4-3-6-7-6 0 4 3 6 7 6z" />
      <path d="M12 13c0-3.5 2.5-5.5 6-5.5 0 3.5-2.5 5.5-6 5.5z" />
      <path d="M5 21h14" />
    </>
  ),
  20: (
    <>
      <rect x="3" y="10" width="5" height="11" />
      <rect x="10" y="5" width="5" height="16" />
      <rect x="17" y="13" width="4" height="8" />
      <path d="M12 8.2h1M12 11.2h1M12 14.2h1M5 13.2h1M5 16.2h1" />
    </>
  ),
  21: (
    <>
      <path d="M5 12a10 10 0 0114 0" />
      <path d="M8 15a6 6 0 018 0" />
      <circle cx="12" cy="18.5" r="1.3" fill="currentColor" />
    </>
  ),
  22: (
    <>
      <ellipse cx="12" cy="13" rx="4.5" ry="5" />
      <path d="M7.7 10.2h8.6M7.3 13h9.4M7.7 15.8h8.6" />
      <circle cx="12" cy="7" r="2" />
      <path d="M7 9l-3-2M17 9l3-2" />
      <path d="M6 11c-2-1-3 1-2 2M18 11c2-1 3 1 2 2" />
    </>
  ),
};

export function PresetIconGlyph({ icon, className }: PresetIconGlyphProps) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden {...COMMON}>
      {PATHS[icon] ?? null}
    </svg>
  );
}
